import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, PhoneCall } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

const SEEN_KEY = "smartsn_exit";
const SEVEN_DAYS = 7 * 86400000;

export const ExitIntent = () => {
  const { lang, t } = useLang();
  const x = t.exitIntent;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (Date.now() - +(localStorage.getItem(SEEN_KEY) || 0) < SEVEN_DAYS) return;
    let idle;
    let armed;
    const show = () => {
      localStorage.setItem(SEEN_KEY, String(Date.now()));
      setOpen(true);
      cleanup();
    };
    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 8) show();
    };
    const resetIdle = () => {
      clearTimeout(idle);
      idle = setTimeout(show, 45000);
    };
    const cleanup = () => {
      document.removeEventListener("mouseout", onMouseOut);
      ["touchstart", "scroll"].forEach((ev) => window.removeEventListener(ev, resetIdle));
      clearTimeout(idle);
      clearTimeout(armed);
    };
    if (window.matchMedia("(pointer: coarse)").matches) {
      ["touchstart", "scroll"].forEach((ev) => window.addEventListener(ev, resetIdle, { passive: true }));
      resetIdle();
    } else {
      armed = setTimeout(() => document.addEventListener("mouseout", onMouseOut), 5000);
    }
    return cleanup;
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/leads/exit-intent", { name, phone, lang });
      setSent(true);
    } catch {
      // keep form
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center px-5 bg-black/70 backdrop-blur-sm"
          data-testid="exit-intent-overlay"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-md bg-[#111111] border border-[#FF7900]/40 p-8"
            data-testid="exit-intent-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              data-testid="exit-intent-close"
              aria-label="Close"
              className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
            {sent ? (
              <p className="text-sm text-emerald-400 py-6" data-testid="exit-intent-thanks">{x.thanks}</p>
            ) : (
              <>
                <span className="inline-flex w-12 h-12 rounded-full bg-[#FF7900]/15 text-[#FF7900] items-center justify-center mb-5">
                  <PhoneCall className="w-5 h-5" />
                </span>
                <h3 className="font-display text-2xl font-medium mb-2">{x.title}</h3>
                <p className="text-sm text-white/55 mb-6">{x.sub}</p>
                <div className="space-y-3 mb-5">
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={x.name}
                    data-testid="exit-intent-name"
                    className="w-full bg-black/40 border border-white/15 px-4 py-3 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300"
                  />
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={x.phone}
                    inputMode="tel"
                    data-testid="exit-intent-phone"
                    className="w-full bg-black/40 border border-white/15 px-4 py-3 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300"
                  />
                </div>
                <button
                  onClick={submit}
                  disabled={busy || name.trim().length < 2 || phone.trim().length < 6}
                  data-testid="exit-intent-submit"
                  className="w-full bg-[#FF7900] hover:bg-white hover:text-black disabled:opacity-40 text-white text-sm font-semibold px-6 py-3.5 rounded-full transition-colors duration-300"
                >
                  {busy ? "…" : x.cta}
                </button>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="exit-intent-no"
                  className="mt-3 w-full text-xs text-white/35 hover:text-white/60 transition-colors duration-200"
                >
                  {x.no}
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
